<!-- NavMenu.vue -->
<template>
  <div>
    <div v-if="navMenuOpen" class="overlay" @click="closeMenu" />
    <div class="nav-menu" :class="[{ active: navMenuOpen }, this.type]" :style="{ width: this.navMenuWidth + 'px', top: navMenuPosition.y + 'px', left: navMenuPosition.x + 'px' }">
      <div class="nav-menu-content">
        <div v-if="type === navMenuTypes.SIGNIN">
          <div class="nav-items">
            <div class="nav-items-main" :class="this.type">
              <div class="item signin" @click="navigateTo('integrations')">Sign in</div>
            </div>
          </div>
        </div>

        <div v-if="type === navMenuTypes.CONTACT">
          <div class="nav-items">
            <div class="nav-items-main">
              <div class="item contact">
                Contact us
                <div class="contact-row">
                  <span class="material-icons-outlined contact-icon">{{ countryCode === 'ZA' ? 'email' : 'phone' }}</span>
                  <a v-if="countryCode === 'ZA'" :href="`mailto:${contactNumber}`">{{ contactNumber }}</a>
                  <a v-else-if="tenantId === 'flippa'" :href="`${contactNumber}`" target="_blank">{{ contactNumber }}</a>
                  <a v-else :href="`tel:${contactNumber}`">{{ contactNumber }}</a>
                  <span class="copy-icon-wrapper" @click.stop="copyContact">
                    <span class="material-icons-outlined copy-icon-default">content_copy</span>
                    <span class="material-icons copy-icon-hover">file_copy</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="type === navMenuTypes.MAIN">
          <div class="company-profile">
            <div class="company-icon" :class="'sme-theme'">
              {{ companyInitials }}
            </div>
            <div class="company-details">
              <h3>{{ companyName }}</h3>
              <span class="body">{{ user && user.email }}</span>
              <span>{{ user && user.contactNumber }}</span>
            </div>
          </div>

          <div class="nav-items">
            <div class="item logout" role="button" @click="logout">
              <span class="material-icons logoff"> logout </span>
              Sign out
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { navMenuTypes } from '@/utils/constants'
import useWhitelabel from '@/composables/useWhitelabel'
import useSegmentAnalytics from '@/composables/segmentTracking'

export default {
  name: 'NavMenu',
  props: {
    type: {
      type: String,
      required: true
    },
    navMenuWidth: {
      type: Number,
      default: 312
    },
    navMenuOpen: {
      type: Boolean,
      default: false
    },
    navMenuPosition: {
      type: Object,
      default: () => ({ x: 0, y: 0 })
    },
    user: {
      type: Object,
      default: () => ({})
    },
    companyName: {
      type: String,
      default: ''
    },
    companyInitials: {
      type: String,
      default: ''
    },
    isXeroRestrictedUser: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { config: globalConfig } = useWhitelabel('global')
    const { trackEvent } = useSegmentAnalytics()

    return { globalConfig, trackEvent }
  },
  computed: {
    navMenuTypes() {
      return navMenuTypes
    },
    contactNumber() {
      return this.globalConfig.contactNumber
    },
    countryCode() {
      return this.globalConfig.countryCode
    },
    tenantId() {
      return this.globalConfig.tenantId
    }
  },
  methods: {
    copyContact() {
      navigator.clipboard.writeText(this.contactNumber)
    },
    navigateTo(name, params) {
      this.$emit('navigate', { name, params })
    },
    logout() {
      this.$emit('logout')
      this.trackEvent('Logged Out', {
        reason: 'User logged out',
        userId: this.user.userId,
        name: this.user.firstName + ' ' + this.user.lastName,
        email: this.user.email
      })
    },
    closeMenu() {
      this.$emit('close-menu')
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/swoop/variables.scss';

.overlay {
  position: fixed;
  z-index: 100;
  height: 100vh;
  width: -webkit-fill-available;
  top: 0;
  right: 0;
  background-color: var(--color-primary-500);
  opacity: 0;
}

.nav-menu {
  width: fit-content !important;
  position: fixed;
  z-index: 1001;
  top: 0;
  right: 0;
  background-color: $color-white;
  transition: all 0.1s linear, padding 0s, border 0s, border-radius 0s left 0s, top 0s;
  padding: 0;
  border: none;
  gap: 8px;

  &.active:not(.contact) {
    left: auto !important;
  }

  & > * {
    display: none;
  }

  &-content {
    width: 100%;
    height: 100%;

    .company-profile {
      width: fit-content !important;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 0 24px 8px;
      text-align: left;
      gap: 16px;

      .company-icon {
        width: 32px;
        height: 32px;
        min-width: 32px;
        min-height: 32px;
        border: 2px solid;
        border-radius: 64px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 500;
      }

      .company-details {
        width: fit-content !important;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        overflow: hidden;

        h3 {
          font-size: 17px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: -0.004em;
          text-align: left;
          margin-bottom: 4px;
          color: $color-black;
        }

        span {
          font-size: 14px;
          font-weight: 400;
          line-height: 16.45px;
          letter-spacing: -0.004em;
          text-align: left;
          color: var(--color-neutral-600);
        }
      }

      p {
        margin: 0;
      }
    }

    .nav-items {
      &-main {
        padding: 16px;

        &.signin {
          padding: 8px 0;
        }
      }

      .hr {
        width: 50px !important;
      }

      .item {
        display: flex;
        align-items: center;
        font-size: 14px;
        line-height: 20px;
        color: #666666;
        height: 38px;

        &.contact {
          width: auto;
          flex-direction: column;
          justify-content: center;
          align-items: flex-start;
          height: auto;
          &:hover {
            background-color: transparent;
            cursor: default;
          }

          .contact-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 8px;

            .contact-icon {
              font-size: 16px;
              width: 16px;
              color: var(--color-primary-500);
            }

            .copy-icon-wrapper {
              display: inline-flex;
              color: #666666;
              cursor: pointer;

              .copy-icon-default,
              .copy-icon-hover {
                font-size: 16px;
                width: 16px;
                margin: 0;
              }

              .copy-icon-hover {
                display: none;
              }

              &:hover {
                color: var(--color-neutral-600);

                .copy-icon-default {
                  display: none;
                }

                .copy-icon-hover {
                  display: inline;
                }
              }
            }
          }

          a {
            margin-top: 0;
            color: var(--color-tertiary-500);
            text-decoration: none;
            font-weight: 500;

            &:hover {
              color: $text-link-hover;
              text-decoration: underline;
            }

            &:active {
              color: var(--color-tertiary-500);
              text-decoration: underline;
            }

            &:focus-visible {
              color: var(--color-tertiary-500);
              text-decoration: underline;
              outline: 2px solid var(--color-tertiary-500);
              outline-offset: 3px;
              border-radius: 3px;
            }
          }
        }

        &.contact,
        &.signin {
          font-size: 14px;
          line-height: 20px;
          letter-spacing: -0.004em;
          color: #666666;

          a {
            text-decoration: none;
            color: $color-tertiary-500;

            &:hover {
              text-decoration: underline;
              color: $text-link-hover;
            }
          }
        }

        &:hover {
          background-color: var(--color-secondary-50);
          cursor: pointer;
        }

        img {
          margin-right: 20px;
        }

        .material-icons {
          margin-right: 12px;
        }
      }

      .logout {
        border-top: 1px solid #dfdfdf;
        width: auto !important;
        color: var(--color-primary);
        padding-top: 8px;

        .logoff {
          color: var(--color-error-700);
        }
      }
    }
  }

  &.active {
    width: auto;
    overflow-x: hidden;
    border-radius: 10px;
    border: 1px solid #ebebeb;
    box-shadow: 0px 4px 4px 0px #00000029;

    &.main {
      padding: 16px;
      padding-bottom: 12px;
      width: auto;
    }

    & > * {
      display: block;
    }
  }

  @media only screen and (max-width: $breakpoint-sm-max) {
    .nav-menu-content span.close {
      display: none;
    }
  }

  @media only screen and (max-width: $breakpoint-xxs) {
    .nav-menu-content .company-profile {
      font-size: 12px;

      & h1 {
        font-size: 14px;
      }
    }
  }
}
</style>
